/**
 * Overlay Position Utilities
 *
 * Handles preset position calculations and transformations for image-based overlays.
 */

import type { OverlayPresetPosition } from '@/types';

/**
 * Convert preset position to normalized 0-1 coordinates
 */
export function presetPositionToCoordinates(preset: OverlayPresetPosition): { x: number; y: number } {
  const positions: Record<OverlayPresetPosition, { x: number; y: number }> = {
    'top-left': { x: 0.02, y: 0.05 },
    'top-center': { x: 0.5, y: 0.05 },
    'top-right': { x: 0.98, y: 0.05 },
    'left-center': { x: 0.05, y: 0.5 },
    'center': { x: 0.5, y: 0.5 },
    'right-center': { x: 0.95, y: 0.5 },
    'bottom-left': { x: 0.02, y: 0.95 },
    'bottom-center': { x: 0.5, y: 0.95 },
    'bottom-right': { x: 0.98, y: 0.95 },
  };
  return positions[preset];
}

/**
 * Get rotation angle for a preset position
 * Assumes the base image arrow points to the right (0°)
 *
 * @param preset - The preset position
 * @param direction - 'inward' or 'outward' arrow direction
 * @returns rotation in degrees
 */
export function getRotationForPresetPosition(
  preset: OverlayPresetPosition,
  direction: 'inward' | 'outward'
): number {
  // Base rotations for INWARD pointing arrows
  // (arrows pointing toward center from each position)
  const inwardRotations: Record<OverlayPresetPosition, number> = {
    'top-left': 135,      // ↘
    'top-center': 180,    // ↓
    'top-right': 225,     // ↙
    'left-center': 90,    // ↓ (rotated from →)
    'center': 0,          // → (no rotation needed)
    'right-center': 270,  // ↑ (rotated from →)
    'bottom-left': 45,    // ↗
    'bottom-center': 0,   // ↑
    'bottom-right': 315,  // ↖
  };

  // For outward arrows, add 180° to point away from center
  const rotation = inwardRotations[preset];
  return direction === 'outward' ? (rotation + 180) % 360 : rotation;
}

/**
 * Get mirroring for a preset position
 * Some positions may need horizontal or vertical flip
 *
 * @param preset - The preset position
 * @returns object with flipX and flipY boolean flags
 */
export function getMirrorForPresetPosition(preset: OverlayPresetPosition): {
  flipX: boolean;
  flipY: boolean;
} {
  // You can customize this based on your arrow graphic design
  // For now, we'll use rotation only and no mirroring
  return {
    flipX: false,
    flipY: false,
  };
}

/**
 * Human-readable labels for preset positions
 */
export function formatPresetPositionName(preset: OverlayPresetPosition): string {
  const names: Record<OverlayPresetPosition, string> = {
    'top-left': 'Top Left',
    'top-center': 'Top Center',
    'top-right': 'Top Right',
    'left-center': 'Left Center',
    'center': 'Center',
    'right-center': 'Right Center',
    'bottom-left': 'Bottom Left',
    'bottom-center': 'Bottom Center',
    'bottom-right': 'Bottom Right',
  };
  return names[preset];
}
